import { html, TemplateResult, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SearchV3UIBase } from './search-v3-ui-base';
import { SearchV3Result } from '../search-v3.interface';

/**
 * Support Search UI Component
 *
 * Extends the base SearchV3UIBase component to provide Support tab specific behavior:
 * - Suggestions are rendered as clickable options (buttons) instead of links
 * - Clicking a suggestion updates the search input and triggers analytics
 * - No navigation occurs, user stays on the same page
 */
@customElement('mhecomm-search-v3-ui-support')
export class SearchV3UISupport extends SearchV3UIBase {
  /**
   * Override the suggestion item rendering for Support tab behavior
   * Renders suggestions as clickable buttons that don't navigate away
   */
  protected renderSuggestionItem(
    suggestion: SearchV3Result,
    highlighted: boolean,
    index: number,
    handleSelect: (item: any) => void
  ): TemplateResult {
    return html`<button
      class="dropdown-item text-start ${highlighted ? 'bg-light' : ''}"
      id="item-${index}"
      role="option"
      aria-selected=${highlighted}
      @click=${() => handleSelect(suggestion)}
      tabindex="-1">
      ${suggestion.term} ${suggestion.label ? html`<span class="text-primary">${suggestion.label}</span>` : nothing}
    </button>`;
  }

  /**
   * Handles selection of suggestion options (Support tab behavior)
   * - Updates search input with selected suggestion
   * - Triggers analytics
   * - Keeps user on the same page (no navigation)
   */
  private handleSuggestionSelection(suggestion: SearchV3Result): void {
    // Update search input with selected suggestion
    this.updateSearchInput?.(suggestion.term);

    // Trigger analytics for Support tab
    this.searchResultClickAnalytic?.(suggestion);

    // Call the parent's search submit handler to trigger search
    this.onSearchTrigger?.();
  }

  /**
   * Override input typeahead to use the Support-specific renderItem method
   */
  protected inputTypeahead() {
    return html`<mhecomm-search-input
      .value=${this.searchInput}
      minSearchChars="3"
      inputElClassList="${this.renderSmallUI ? 'border rounded-start border-gray600' : ''}"
      class="form-control position-relative ${this.renderSmallUI ? 'border-0' : 'search-border'}"
      .updateSearchInput=${this.updateSearchInput?.bind(this)}
      .fetchFn=${this.onSearchInputChange.bind(this)}
      .getLabel=${(suggestion: any) => suggestion.term}
      .renderItem=${this.renderSuggestionItem.bind(this)}
      .onSelect=${this.handleSuggestionSelection.bind(this)}
      placeholder=${this.searchPlaceholder}
      ?hideSuggestion=${this.searchInput?.length <= 2}
      .blockEnterOnPendingInput=${false}></mhecomm-search-input>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v3-ui-support': SearchV3UISupport;
  }
}
